import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import StarRating from './StarRating';
import { MessageSquare, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsList = ({ userId, showTitle = true, limit = 5 }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadReviews();
  }, [userId]);

  const loadReviews = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/reviews`, {
        withCredentials: true
      });
      setReviews(response.data.reviews);
      // Brief LOT2.4 — mapping défensif : show_rating absent ≠ masqué
      // (l'ancien contrat backend ne renvoyait pas ces champs → modal vide).
      setStats({
        total: response.data.total,
        average: response.data.average_rating ?? response.data.average ?? 0,
        count: response.data.rating_count ?? response.data.total ?? 0,
        distribution: response.data.distribution ?? response.data.breakdown ?? {},
        showRating: response.data.show_rating !== false,
      });
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
      </div>
    );
  }

  // If user has hidden their rating
  if (stats && !stats.showRating) {
    return null;
  }

  // No reviews yet
  if (!reviews || reviews.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="reviews-list">
        {showTitle && (
          <h2 className="text-xl font-semibold text-gray-900 flex items-center mb-4">
            <MessageSquare className="h-5 w-5 mr-2 text-purple-500" />
            Avis
          </h2>
        )}
        <p className="text-gray-400 text-center py-4">
          Aucun avis pour le moment
        </p>
      </div>
    );
  }

  const displayedReviews = expanded ? reviews : reviews.slice(0, limit);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="reviews-list">
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MessageSquare className="h-5 w-5 mr-2 text-purple-500" />
            Avis
          </h2>
          
          {stats && (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {stats.average?.toFixed(1) || 0}
              </span>
              <div>
                <StarRating rating={stats.average || 0} size="sm" />
                <p className="text-xs text-gray-500">{stats.count} avis</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rating Distribution (optional) */}
      {stats?.distribution && expanded && (
        <div className="mb-6 p-4 bg-gray-50 rounded-xl">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = stats.distribution[star] || 0;
            const percent = stats.total > 0 ? (count / stats.total) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2 mb-1">
                <span className="text-xs text-gray-500 w-3">{star}</span>
                <StarRating rating={star} size="sm" maxRating={1} />
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-400 rounded-full transition-all"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 w-6">{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        {displayedReviews.map((review) => (
          <div 
            key={review.review_id} 
            className="border-b border-gray-100 last:border-0 pb-4 last:pb-0"
            data-testid={`review-${review.review_id}`}
          >
            <div className="flex items-start gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={review.reviewer?.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
                  {review.reviewer?.display_name?.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-gray-900">
                    {review.reviewer?.display_name || 'Utilisateur'}
                  </p>
                  <StarRating rating={review.rating} size="sm" />
                </div>
                
                {review.comment && (
                  <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                    {review.comment}
                  </p>
                )}
                
                <p className="text-xs text-gray-400 mt-2">
                  {review.created_at && formatDistanceToNow(new Date(review.created_at), { 
                    addSuffix: true,
                    locale: fr 
                  })}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show More / Less */}
      {reviews.length > limit && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-center w-full mt-4 py-2 text-sm text-purple-600 hover:text-purple-700 transition-colors"
          data-testid="toggle-reviews"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Voir moins
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Voir les {reviews.length - limit} autres avis
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ReviewsList;
